import { Button, Card, StatusChip } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, CircleAlert, Clapperboard, FileText, Plus, Sparkles, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDateTime } from "../../lib/format";
import { useSession } from "../../lib/session";

type View = components["schemas"]["PlanViewOut"];
type Plan = components["schemas"]["PlanOut"];
type Item = components["schemas"]["PlanItemOut"];
type ItemIn = components["schemas"]["PlanItemIn"];
type ItemUpdate = components["schemas"]["PlanItemUpdateIn"];
type PlanUpdate = components["schemas"]["PlanUpdateIn"];

const TYPES = ["VIDEO", "IMAGE", "CAROUSEL", "MANUAL_SHOOT"] as const;
const METHODS = ["MANUAL", "AI", "HYBRID"] as const;
const TARGETS = ["META_REELS", "META_FEED", "INSTAGRAM", "TIKTOK", "CUSTOM"] as const;
const ACT = { submit: "submitCreativePlan", ret: "returnPlanToDraft", approve: "approveCreativePlan",
  edit: "edit", create: "createPlan", version: "newVersion" } as const;
const AWAITING = "AWAITING_AI_PRODUCTION_PHASE";
const HYP_SHOWN = ["angle", "hook_direction", "desired_emotional_transition", "proof_strategy", "cta_direction"] as const;
const F = { audience: "audience_text", cta: "cta", offer: "offer", proof: "proof_strategy", notes: "notes",
  offerRelevant: "offer_relevant", proofRelevant: "proof_relevant" } as const;
const DRAFT = "DRAFT";

/** Creative Plan Builder (doc 04 P8) while DRAFT; Plan Detail (P9) once submitted or approved. */
export function PlanPage() {
  const { displayId = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["plan", displayId];
  const q = useQuery({ queryKey: key,
    queryFn: () => call<View>(api.GET("/app/v1/projects/{display_id}/plan", { params: { path: { display_id: displayId } } })) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["project", displayId] });
  };
  const create = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/plan", { params: { path: { display_id: displayId } } })),
    onSuccess: refresh,
  });
  return (
    <QueryState query={q}>
      {(v) => {
        const p = v.plan;
        const editable = v.allowed_actions.includes(ACT.edit);
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/projects" className="hover:text-ink">{t("nav.projects")}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <Link to={`/projects/${v.display_id}`} className="hover:text-ink">{v.display_id}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{t("plan.title_plain")}</span>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{t("plan.title_plain")}</h1>
                  {p && <PlanStatus status={p.status} />}
                  {p && <span className="rounded-full border border-line px-2.5 py-0.5 text-[12px] text-ink-2">{t("plan.version_n", { n: p.version })}</span>}
                </div>
                <p className="mt-1 text-[15px] text-ink-2">{t("plan.subtitle")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to={`/projects/${v.display_id}/strategy`} className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-4 text-sm font-medium text-ink hover:bg-elevated">
                  <Sparkles aria-hidden="true" className="size-4" />{t("plan.back_to_karya")}
                </Link>
                {(v.allowed_actions.includes(ACT.create) || v.allowed_actions.includes(ACT.version)) && (
                  <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}>
                    <Plus aria-hidden="true" className="size-4" />{t(v.allowed_actions.includes(ACT.create) ? "plan.create" : "plan.new_version")}
                  </Button>
                )}
              </div>
            </div>
            <ErrorLine error={create.error} />
            {!p ? (
              <Card className="p-8 text-center text-[14px] text-ink-2">
                {v.kept_hypotheses.length === 0 ? t("plan.empty_no_kept") : t("plan.empty")}
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="min-w-0 space-y-6">
                  <Hypotheses v={v} p={p} editable={editable} onDone={refresh} />
                  <Basics v={v} p={p} editable={editable} onDone={refresh} />
                  <Deliverables v={v} p={p} editable={editable} onDone={refresh} />
                  {v.jobs.length > 0 && <Jobs v={v} />}
                </div>
                <aside className="space-y-6">
                  <Readiness v={v} p={p} onDone={refresh} />
                  <History v={v} p={p} />
                </aside>
              </div>
            )}
          </div>
        );
      }}
    </QueryState>
  );
}

function PlanStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone = status === "APPROVED_FOR_PRODUCTION" ? "success" : status === "CP_REVIEW" ? "review" : status === DRAFT ? "planning" : "neutral";
  return <StatusChip tone={tone} icon={<FileText className="size-3.5" />} label={t(`plan.status_${status.toLowerCase()}`)} />;
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="text-[14px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

function Section({ title, icon, children, action }: { title: string; icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[18px] font-semibold text-ink">{icon}{title}</h2>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </Card>
  );
}

type Props = { v: View; p: Plan; editable: boolean; onDone: () => void };

function Hypotheses({ v, p, editable, onDone }: Props) {
  const { t } = useTranslation();
  const selected = new Set(p.hypotheses.map((h) => h.id));
  const m = useMutation({
    mutationFn: (ids: string[]) => call(api.PUT("/app/v1/projects/{display_id}/plan/{plan_id}/hypotheses", {
      params: { path: { display_id: v.display_id, plan_id: p.id } }, body: { hypothesis_ids: ids } })),
    onSuccess: onDone,
  });
  const toggle = (id: string) => {
    const next = selected.has(id) ? [...selected].filter((x) => x !== id) : [...selected, id];
    m.mutate(next);
  };
  const choices = editable ? v.kept_hypotheses.filter((h) => h.status === "KEPT" || selected.has(h.id)) : [];
  return (
    <Section title={t("plan.selected_hypotheses")} icon={<Sparkles aria-hidden="true" className="size-5 text-violet" />}>
      {editable && choices.length > 0 && (
        <fieldset className="mb-4">
          <legend className="mb-2 text-[13px] text-ink-2">{t("plan.choose_hypotheses")}</legend>
          <div className="flex flex-wrap gap-2">
            {choices.map((h) => (
              <label key={h.id} className="flex items-center gap-2 rounded-full border border-line px-3 py-1.5 text-[13px] text-ink">
                <input type="checkbox" checked={selected.has(h.id)} disabled={m.isPending} onChange={() => toggle(h.id)} />{h.name}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {p.hypotheses.length === 0 ? (
        <p className="text-[14px] text-ink-2">{t("plan.no_hypotheses")}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {p.hypotheses.map((h) => (
            <li key={h.id} className="rounded-[var(--radius-card)] border border-line p-4">
              <div className="font-medium text-ink">{h.name}</div>
              <dl className="mt-2 space-y-1 text-[13px]">
                {HYP_SHOWN.map((f) => (
                  <div key={f} className="flex gap-2">
                    <dt className="w-36 shrink-0 text-ink-2">{t(`karya.hyp_${f}`)}</dt>
                    <dd className={h[f] ? "text-ink" : "text-[var(--status-warning)]"}>{h[f] ?? t("requests.not_provided")}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      )}
      <ErrorLine error={m.error} />
    </Section>
  );
}

function Basics({ v, p, editable, onDone }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, string | boolean>>({});
  const m = useMutation({
    mutationFn: (changes: Partial<PlanUpdate>) => call(api.PATCH("/app/v1/projects/{display_id}/plan/{plan_id}", {
      params: { path: { display_id: v.display_id, plan_id: p.id } }, body: { ...changes, row_version: p.row_version } })),
    onSuccess: () => { setDraft({}); onDone(); },
  });
  const val = (k: keyof Plan) => (k in draft ? draft[k] : p[k]) as string | boolean | null;
  const save = (k: keyof PlanUpdate, value: string | boolean) => {
    if (value === p[k as keyof Plan]) return;
    m.mutate({ [k]: typeof value === "string" ? (value.trim() || null) : value } as Partial<PlanUpdate>);
  };
  const text = (k: "audience_text" | "cta" | "offer" | "proof_strategy" | "notes", rows = 1) => {
    const id = `plan-${k}`;
    const common = { id, value: String(val(k) ?? ""), disabled: !editable || m.isPending,
      onChange: (e: { target: { value: string } }) => setDraft({ ...draft, [k]: e.target.value }),
      onBlur: (e: { target: { value: string } }) => save(k, e.target.value),
      className: "w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2 text-[14px] text-ink disabled:bg-elevated" };
    return (
      <div>
        <label htmlFor={id} className="mb-1 block text-[13px] font-medium text-ink-2">{t(`plan.field_${k}`)}</label>
        {rows > 1 ? <textarea rows={rows} {...common} /> : <input {...common} />}
      </div>
    );
  };
  const flag = (k: "offer_relevant" | "proof_relevant") => (
    <label className="mt-2 flex items-center gap-2 text-[13px] text-ink-2">
      <input type="checkbox" checked={Boolean(val(k))} disabled={!editable || m.isPending} onChange={(e) => save(k, e.target.checked)} />
      {t(`plan.field_${k}`)}
    </label>
  );
  return (
    <Section title={t("plan.basics")} icon={<FileText aria-hidden="true" className="size-5 text-violet" />}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">{text(F.audience, 2)}</div>
        {text(F.cta)}
        <div>{text(F.offer)}{flag(F.offerRelevant)}</div>
        <div className="md:col-span-2">{text(F.proof, 2)}{flag(F.proofRelevant)}</div>
        <div className="md:col-span-2">{text(F.notes, 2)}</div>
      </div>
      <ErrorLine error={m.error} />
    </Section>
  );
}

function Deliverables({ v, p, editable, onDone }: Props) {
  const { t } = useTranslation();
  const add = useMutation({
    mutationFn: (body: ItemIn) => call(api.POST("/app/v1/projects/{display_id}/plan/{plan_id}/items", {
      params: { path: { display_id: v.display_id, plan_id: p.id } }, body })),
    onSuccess: onDone,
  });
  return (
    <Section title={t("plan.deliverables")} icon={<Clapperboard aria-hidden="true" className="size-5 text-violet" />}
      action={editable && (
        <Button variant="secondary" className="h-9" onClick={() => add.mutate({ deliverable_type: "VIDEO", quantity: 1, distribution_targets: [] })}>
          <Plus aria-hidden="true" className="size-4" />{t("plan.add_deliverable")}
        </Button>
      )}>
      {p.items.length === 0 ? <p className="text-[14px] text-ink-2">{t("plan.no_deliverables")}</p> : (
        <ul className="space-y-3">
          {p.items.map((i) => <ItemRow key={i.id + i.row_version} v={v} p={p} item={i} editable={editable} onDone={onDone} />)}
        </ul>
      )}
      <ErrorLine error={add.error} />
    </Section>
  );
}

function ItemRow({ v, p, item, editable, onDone }: { v: View; p: Plan; item: Item; editable: boolean; onDone: () => void }) {
  const { t } = useTranslation();
  const path = { display_id: v.display_id, plan_id: p.id, item_id: item.id };
  const m = useMutation({
    mutationFn: (changes: Partial<ItemUpdate>) => call(api.PATCH("/app/v1/projects/{display_id}/plan/{plan_id}/items/{item_id}", {
      params: { path }, body: { ...changes, row_version: item.row_version } })),
    onSuccess: onDone,
  });
  const rm = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/plan/{plan_id}/items/{item_id}/remove", {
      params: { path }, body: { row_version: item.row_version } })),
    onSuccess: onDone,
  });
  const [format, setFormat] = useState(item.preferred_format ?? "");
  const n = item.position + 1;
  const select = "h-9 rounded-[var(--radius-control)] border border-line bg-surface px-2 text-[13px] text-ink disabled:bg-elevated";
  const busy = !editable || m.isPending;
  const missingMethod = !item.production_method;
  return (
    <li className="rounded-[var(--radius-card)] border border-line p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={`type-${item.id}`} className="mb-1 block text-[12px] text-ink-2">{t("plan.item_type", { n })}</label>
          <select id={`type-${item.id}`} className={select} disabled={busy} value={item.deliverable_type}
            onChange={(e) => m.mutate({ deliverable_type: e.target.value as ItemUpdate["deliverable_type"] })}>
            {TYPES.map((x) => <option key={x} value={x}>{t(`plan.type_${x.toLowerCase()}`)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`qty-${item.id}`} className="mb-1 block text-[12px] text-ink-2">{t("plan.quantity")}</label>
          <input id={`qty-${item.id}`} type="number" min={1} max={100} className={`${select} w-20`} disabled={busy} defaultValue={item.quantity}
            onBlur={(e) => { const q = Number(e.target.value); if (q !== item.quantity && q >= 1 && q <= 100) m.mutate({ quantity: q }); }} />
        </div>
        <fieldset>
          <legend className="mb-1 text-[12px] text-ink-2">{t("plan.method")}</legend>
          <div className={`flex overflow-hidden rounded-[var(--radius-control)] border ${missingMethod ? "border-[var(--status-warning)]" : "border-line"}`}>
            {METHODS.map((x) => (
              <label key={x} className={`cursor-pointer px-3 py-1.5 text-[13px] ${item.production_method === x ? "bg-[var(--accent-primary-soft)] font-semibold text-violet-text" : "bg-surface text-ink"}`}>
                <input type="radio" className="sr-only" name={`method-${item.id}`} checked={item.production_method === x} disabled={busy}
                  onChange={() => m.mutate({ production_method: x })} />
                {t(`plan.method_${x.toLowerCase()}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor={`fmt-${item.id}`} className="mb-1 block text-[12px] text-ink-2">{t("plan.format")}</label>
          <input id={`fmt-${item.id}`} className={`${select} w-24`} disabled={busy} value={format} maxLength={120}
            onChange={(e) => setFormat(e.target.value)} onBlur={() => format !== (item.preferred_format ?? "") && m.mutate({ preferred_format: format.trim() || null })} />
        </div>
        {p.hypotheses.length > 0 && (
          <div>
            <label htmlFor={`hyp-${item.id}`} className="mb-1 block text-[12px] text-ink-2">{t("plan.item_hypothesis")}</label>
            <select id={`hyp-${item.id}`} className={select} disabled={busy} value={item.hypothesis_id ?? ""}
              onChange={(e) => m.mutate({ hypothesis_id: e.target.value || null })}>
              <option value="">{t("plan.item_hypothesis_none")}</option>
              {p.hypotheses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
        )}
        {editable && (
          <button type="button" aria-label={t("plan.remove_deliverable", { n })} onClick={() => rm.mutate()}
            className="ml-auto flex size-9 items-center justify-center rounded-[var(--radius-control)] text-ink-2 hover:bg-elevated hover:text-ink">
            <X aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
      <fieldset className="mt-3">
        <legend className="mb-1 text-[12px] text-ink-2">{t("plan.distribution")}</legend>
        <div className="flex flex-wrap gap-2">
          {TARGETS.map((x) => {
            const on = item.distribution_targets.includes(x);
            return (
              <label key={x} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] ${on ? "border-[var(--accent-primary)] text-violet-text" : "border-line text-ink-2"}`}>
                <input type="checkbox" className="sr-only" checked={on} disabled={busy}
                  onChange={() => m.mutate({ distribution_targets: (on ? item.distribution_targets.filter((y) => y !== x) : [...item.distribution_targets, x]) as ItemUpdate["distribution_targets"] })} />
                {on && <Check aria-hidden="true" className="size-3" />}{t(`plan.target_${x.toLowerCase()}`)}
              </label>
            );
          })}
        </div>
      </fieldset>
      {item.capability_state === AWAITING && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--accent-secondary-soft)] px-3 py-1 text-[12px] font-semibold text-magenta-text">
          {t("honest.awaiting_ai_production_phase")}
        </p>
      )}
      {editable && item.production_method && item.production_method !== "MANUAL" && item.deliverable_type !== "MANUAL_SHOOT" && (
        <p className="mt-2 text-[12px] text-ink-2">{t("plan.ai_note")}</p>
      )}
      <ErrorLine error={m.error ?? rm.error} />
    </li>
  );
}

function Jobs({ v }: { v: View }) {
  const { t } = useTranslation();
  return (
    <Section title={t("plan.jobs")} icon={<Clapperboard aria-hidden="true" className="size-5 text-violet" />}
      action={<Link to="/production" className="text-[13px] text-violet-text hover:underline">{t("plan.open_board")}</Link>}>
      <ul className="divide-y divide-[var(--border-default)]">
        {v.jobs.map((j) => (
          <li key={j.id} className="flex items-center justify-between gap-3 py-2 text-[14px]">
            <Link to={`/production/jobs/${j.display_id}`} className="font-medium text-ink hover:underline">{j.display_id}</Link>
            <span className="min-w-0 flex-1 truncate text-ink-2">{t(`plan.job_type_${j.job_type.toLowerCase()}`)} · {j.title}</span>
            <span className="text-[12px] text-ink-2">{t(`status.production_job.${j.status.toLowerCase()}`, { defaultValue: j.status })}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function issueLabel(t: (k: string, o?: Record<string, unknown>) => string, i: { field: string; rule: string; item: number | null }, p: Plan) {
  if (i.field.startsWith("hypothesis:")) {
    const [, id, f] = i.field.split(":");
    const name = p.hypotheses.find((h) => h.id === id)?.name ?? "";
    return t("plan.issue_hypothesis_field", { name, field: t(`karya.hyp_${f}`) });
  }
  if (i.item) return t(`plan.issue_${i.field}`, { n: i.item });
  return t(`plan.issue_${i.field}`);
}

function Readiness({ v, p, onDone }: { v: View; p: Plan; onDone: () => void }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<null | "approve" | "return">(null);
  const [reason, setReason] = useState("");
  const path = { display_id: v.display_id, plan_id: p.id };
  const submit = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/plan/{plan_id}/submit", { params: { path }, body: { row_version: p.row_version } })),
    onSuccess: onDone,
  });
  const approve = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/plan/{plan_id}/approve", { params: { path }, body: { row_version: p.row_version } })),
    onSuccess: () => { setDialog(null); onDone(); },
  });
  const ret = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/plan/{plan_id}/return", { params: { path }, body: { row_version: p.row_version, reason: reason.trim() || null } })),
    onSuccess: () => { setDialog(null); setReason(""); onDone(); },
  });
  const manual = p.items.filter((i) => i.production_method === "MANUAL" || i.production_method === "HYBRID").length;
  const ai = p.items.filter((i) => (i.production_method === "AI" || i.production_method === "HYBRID") && i.deliverable_type !== "MANUAL_SHOOT").length;
  const can = (a: string) => v.allowed_actions.includes(a);
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("plan.readiness")}</h2>
      {p.issues.length === 0 ? (
        <p className="mt-3 flex items-center gap-2 text-[14px] text-[var(--status-success)]"><Check aria-hidden="true" className="size-4" />{t("plan.ready")}</p>
      ) : (
        <ul className="mt-3 space-y-1.5" aria-label={t("plan.issues")}>
          {p.issues.map((i, n) => (
            <li key={n} className="flex items-start gap-2 text-[13px] text-ink">
              <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-[var(--status-warning)]" />{issueLabel(t, i, p)}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-5 flex flex-col gap-2">
        {can(ACT.submit) && (
          <Button variant="primary" disabled={p.issues.length > 0 || submit.isPending} onClick={() => submit.mutate()}>{t("plan.submit")}</Button>
        )}
        {can(ACT.approve) && (
          <Button variant="primary" onClick={() => setDialog("approve")}>{t("plan.approve")}</Button>
        )}
        {can(ACT.ret) && <Button variant="secondary" onClick={() => setDialog("return")}>{t("plan.return")}</Button>}
        {p.status === "APPROVED_FOR_PRODUCTION" && v.replan_blocked && <p className="text-[12px] text-ink-2">{t("plan.replan_blocked")}</p>}
      </div>
      <ErrorLine error={submit.error} />
      <Dialog open={dialog === "approve"} onOpenChange={(o) => !o && setDialog(null)} title={t("plan.approve_title")} description={t("plan.approve_body")}>
        <ul className="space-y-1 text-[14px] text-ink">
          <li>{t("plan.approve_jobs", { count: manual })}</li>
          <li>{t("plan.approve_ai", { count: ai })}</li>
        </ul>
        <ErrorLine error={approve.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={approve.isPending} onClick={() => approve.mutate()}>{t("plan.approve")}</Button>
        </div>
      </Dialog>
      <Dialog open={dialog === "return"} onOpenChange={(o) => !o && setDialog(null)} title={t("plan.return_title")}>
        <label htmlFor="return-reason" className="mb-1 block text-[13px] font-medium text-ink-2">{t("plan.return_reason")}</label>
        <textarea id="return-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
        <ErrorLine error={ret.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={ret.isPending} onClick={() => ret.mutate()}>{t("plan.return")}</Button>
        </div>
      </Dialog>
    </Card>
  );
}

function History({ v, p }: { v: View; p: Plan }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("plan.history")}</h2>
      <ul className="mt-3 space-y-2 text-[13px]">
        {v.versions.map((x) => (
          <li key={x.id} className="flex items-center justify-between gap-2">
            <span className="text-ink">{t("plan.version_n", { n: x.version })}</span>
            <PlanStatus status={x.status} />
          </li>
        ))}
      </ul>
      {p.approved_at && (
        <p className="mt-3 text-[12px] text-ink-2">
          {t("plan.approved_by_at", { name: p.approved_by ?? "", at: formatDateTime(new Date(p.approved_at), i18n.language, me.time_zone) })}
        </p>
      )}
      {p.return_reason && <p className="mt-3 text-[12px] text-ink-2">{t("plan.returned_because", { reason: p.return_reason })}</p>}
    </Card>
  );
}
